import React from "react";
import { Button } from "@/components/ui/button";
import { Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

/**
 * Universal share button — Phase 5.
 *
 * Prefers the Web Share API so the user gets the native OS picker (iOS
 * "share sheet", Android intent chooser, desktop browser action). Falls
 * back to `navigator.clipboard.writeText()` when Web Share is not
 * available (mostly desktop Firefox / older browsers) or when the user
 * dismisses the native sheet with an error other than AbortError.
 *
 * Two ways to compute the link:
 *  1) Preferred: pass a raw `path` (starting with "/") — we build the
 *     absolute URL from window.location.origin.
 *  2) Legacy: pass `reportId` — kept for backward compat with iter51.
 *
 * The caller is responsible for showing this component ONLY on publicly
 * viewable content. We never generate a shareable link server-side; this
 * button just wraps whatever URL the caller passed in, so private chats,
 * pending moderation, hidden or removed content are inherently unshareable
 * because their pages never render the button.
 */
export default function ShareButton({ reportId, path, title, description, testid = "share-btn", size = "sm", variant = "outline" }) {
  const { t } = useI18n();
  const resolvedPath = path || (reportId ? `/r/${reportId}` : "/");
  const publicUrl = `${window.location.origin}${resolvedPath}`;
  const share = async () => {
    const payload = { title: title || "ToClean", text: description || "", url: publicUrl };
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    // Fallback: copy link
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t("copy_link_ok"));
    } catch {
      toast.error(t("copy_link_err"));
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t("copy_link_ok"));
    } catch {
      toast.error(t("copy_link_err"));
    }
  };
  return (
    <div className="inline-flex items-center gap-1">
      <Button data-testid={testid} onClick={share} size={size} variant={variant} className="rounded-full hover:bg-emerald-50 hover:text-emerald-800">
        <Share2 size={14} className="mr-1.5" /> {t("share")}
      </Button>
      <Button data-testid={`${testid}-copy`} onClick={copy} size={size} variant="ghost" className="rounded-full hover:bg-slate-100" title={t("copy_link")}>
        <Copy size={14} />
      </Button>
    </div>
  );
}
